import { useState } from 'react';
import { getExpressPaymentMethodsAvailability } from '../../components/ExpressCheckout/getExpressPaymentMethodsAvailability';
import { ExpressPaymentMethodStatus } from '../../models/expressCheckout';

export const useOnExpressPaymentMethodsFullyLoaded = ({
    allowApplePay,
    allowGooglePay,
    allowPayPal,
    applePayExpressPaymentMethodStatus,
    areAllSelectedTicketsFree,
    areTicketsSelected,
    googlePayExpressPaymentMethodStatus,
    onExpressPaymentMethodsFullyLoaded,
    paypalExpressPaymentMethodStatus,
    hasAsyncApplePayErrors,
    areEventbriteLegacyPaymentMethodsDisabled,
}: {
    allowApplePay: boolean;
    allowGooglePay: boolean;
    allowPayPal: boolean;
    applePayExpressPaymentMethodStatus: ExpressPaymentMethodStatus;
    areAllSelectedTicketsFree: boolean;
    areTicketsSelected: boolean;
    googlePayExpressPaymentMethodStatus: ExpressPaymentMethodStatus;
    onExpressPaymentMethodsFullyLoaded: () => void;
    paypalExpressPaymentMethodStatus: ExpressPaymentMethodStatus;
    hasAsyncApplePayErrors: boolean;
    areEventbriteLegacyPaymentMethodsDisabled: boolean;
}) => {
    const [hasCalledBack, setHasCalledBack] = useState(false);
    if (hasCalledBack) {
        return;
    }

    // Early exit optimization: When legacy payment methods are disabled, so no payment methods
    // Immediately invoke the callback to signal that "loading" is complete.
    if (areEventbriteLegacyPaymentMethodsDisabled) {
        onExpressPaymentMethodsFullyLoaded();
        setHasCalledBack(true);
        return;
    }

    const {
        shouldShowApplePayButton,
        shouldShowGooglePayButton,
        shouldShowPaypalButton,
    } = getExpressPaymentMethodsAvailability({
        areAllSelectedTicketsFree,
        areTicketsSelected,
        hasAsyncApplePayErrors,
        isExpressApplePayEnabled: allowApplePay,
        isExpressGooglePayEnabled: allowGooglePay,
        isExpressPayPalEnabled: allowPayPal,
        areEventbriteLegacyPaymentMethodsDisabled,
    });

    const isLoadingExpressCheckout = [
        {
            shouldShow: shouldShowApplePayButton,
            status: applePayExpressPaymentMethodStatus,
        },
        {
            shouldShow: shouldShowGooglePayButton,
            status: googlePayExpressPaymentMethodStatus,
        },
        {
            shouldShow: shouldShowPaypalButton,
            status: paypalExpressPaymentMethodStatus,
        },
    ].some(({ shouldShow, status }) => {
        if (!shouldShow) {
            return (
                status ===
                ExpressPaymentMethodStatus.availabilityNotEvaluatedYet
            );
        }

        return status !== ExpressPaymentMethodStatus.fullyLoaded;
    });

    if (isLoadingExpressCheckout) {
        return;
    }

    onExpressPaymentMethodsFullyLoaded();
    setHasCalledBack(true);
};
